/**
 * Where a Journeys node drills down to, and whether it drills down at all.
 *
 * Pure, and kept out of the chart component so it can be tested without pulling
 * in the charting library.
 */

/** The traces-page query param each node type filters on. */
export const TRACE_FILTER_PARAM_BY_NODE_TYPE = {
  AGENT: 'agent',
  TOOL: 'tool',
  MODEL: 'model',
  LLM: 'model',
  WORKFLOW: 'workflow',
  OUTCOME: 'outcome',
};

/**
 * Can clicking this node's band lead anywhere?
 *
 * The traces page is scoped to one service and filters on a bare name, so a node
 * from another service has nothing to drill into: the selected service's traces
 * never list a remote agent, and a bare name is ambiguous across services
 * anyway. Showing a pointer and then doing nothing is worse than not offering
 * the click, so the cursor follows this too.
 *
 * The details drawer is unaffected and still opens for a remote agent: it
 * already queries the node's own service.
 */
export function traceFilterKey(node) {
  if (node.is_remote) return undefined;
  return TRACE_FILTER_PARAM_BY_NODE_TYPE[node.nodeType || node.type];
}

export function isNodeClickable(node) {
  return Boolean(traceFilterKey(node));
}

/** How far a rendered rect may sit from its layout position and still be it. */
const RECT_MATCH_TOLERANCE_PX = 0.5;

/**
 * Is the rect at (x, y) one of the nodes that leads nowhere?
 *
 * The chart container only sees the <rect> under the pointer, not which node it
 * belongs to, so position is what identifies it. The charting library renders a
 * node at its own layout coordinates, hence the match rather than an id lookup,
 * and the tolerance absorbs float formatting rather than any real offset.
 *
 * `boxes` is [{ x, y }] for the inert nodes, collected from the layout.
 */
export function isInertNodeRect(x, y, boxes) {
  if (!boxes || !boxes.length) return false;
  // No NaN guard: a rect with no x/y attribute gives NaN, and every NaN
  // comparison is false, so it matches nothing on its own. Pinned by a test.
  return boxes.some(
    (b) => Math.abs(b.x - x) < RECT_MATCH_TOLERANCE_PX
        && Math.abs(b.y - y) < RECT_MATCH_TOLERANCE_PX
  );
}
